import React, { useCallback, useEffect, useReducer, useState, useMemo } from "react";
import { Drawer, Paper, Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import Header from "./Header";
import InfoSection from "./InfoSection";
import ScrollableContent from "./ScrollableContent";
import { fetchMCPToolsList } from "../../utils/api";
import { fetchExecutionDetails } from "../../utils/api/testlaunch_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import ExecutionResultSteps from "../AutomationPage/ExecutionResultSteps";
import ExecutionSummaryHeader from "../AutomationPage/ExecutionSummaryHeader";
import SessionIdCopyButton from '../common/SessionIdCopyButton';
import { detectAiDynamicExecution } from "../../utils/executionRunMode";
import { sanitizeExecutionErrorMessage } from "../../utils/automation/userFacingErrors";

const initialState = {
  data: null,
  isLoadingExecutionDetails: false,
};
function reducer(state, action) {
  switch (action.type) {
    case "SET_LOADING_EXECUTION_DETAILS":
      return { ...state, isLoadingExecutionDetails: action.payload };

    case "SET_DATA":
      return { ...state, data: action.payload };

    default:
      return state;
  }
}
const TestLaunchExecutionDetails = ({ onClose, componentData }) => {

  const [state, dispatch] = useReducer(reducer, initialState);
  const { id: executionResultId, testCaseId } = componentData?.data || {};
  const { isModalOpen } = componentData || {};
  const { data, isLoadingExecutionDetails } = state;
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const [mcpToolsList, setMcpToolsList] = useState([]);

  const fetchExecutionDetailsReport = useCallback(async () => {
    dispatch({ type: "SET_LOADING_EXECUTION_DETAILS", payload: true });
    try {
      const response = await fetchExecutionDetails({ executionResultId });
      if (response.status === STATUS.SUCCESS) {
        dispatch({ type: "SET_DATA", payload: response.data });
      }
    } finally {
      dispatch({ type: "SET_LOADING_EXECUTION_DETAILS", payload: false });
    }
  }, [executionResultId]);

  useEffect(() => {
    fetchExecutionDetailsReport();
  }, [fetchExecutionDetailsReport]);

  // Fetch MCP tools list
  useEffect(() => {
    const fetchMCPTools = async () => {
      try {
        const response = await fetchMCPToolsList();
        if (response?.data) {
          setMcpToolsList(response.data);
        }
      } catch (error) {
        console.error('Error fetching MCP tools:', error);
      }
    };
    fetchMCPTools();
  }, []);

  // if (isLoadingExecutionDetails) {
  //   return (
  //     <Box
  //       sx={{
  //         p: 4,
  //         minHeight: "80vh",
  //         display: "flex",
  //         flexDirection: "column",
  //         alignItems: "center",
  //         justifyContent: "center",
  //       }}
  //     >
  //       <CircularProgress size={48} thickness={4} color="primary" />
  //     </Box>
  //   );
  // }

  const {
    key,
    title,
    status,
    duration,
    executionStartTime,
    lastFiveExecution,
    executionSteps,
    executionSummary,
    automationStatus,
    execution_steps,
    sessionId
  } = data || {};

  const headerSubtitle = key ? `${key} - ${title}` : title || "";

  const isAiDynamicRun = useMemo(() => detectAiDynamicExecution(data), [data]);

  // Calculate test summary from execution steps
  const testSummary = useMemo(() => {
    const steps = execution_steps?.length > 0 ? execution_steps : executionSteps || [];
    if (!steps || steps.length === 0) {
      const summaryFailed = Number(executionSummary?.failed) || 0;
      if (summaryFailed > 0) {
        return {
          totalSteps: executionSummary?.total ?? summaryFailed,
          passed: executionSummary?.completed ?? 0,
          failed: summaryFailed,
        };
      }
      return { totalSteps: 0, passed: 0, failed: 0 };
    }

    const passed = steps.filter((step) => step.passed === true).length;
    const failed = steps.filter((step) => step.passed === false).length;

    return {
      totalSteps: steps.length,
      passed,
      failed,
    };
  }, [execution_steps, executionSteps, executionSummary]);

  const planFetchEmptyPanelMessage = useMemo(() => {
    const steps = execution_steps?.length > 0 ? execution_steps : executionSteps || [];
    if (steps.length > 0) return null;
    if (executionSummary?.FAIL_TYPE || executionSummary?.errorMessage) {
      return sanitizeExecutionErrorMessage(executionSummary?.errorMessage);
    }
    return null;
  }, [execution_steps, executionSteps, executionSummary]);

  // Create mcpToolMap from mcpToolsList
  const mcpToolMap = useMemo(() => {
    return mcpToolsList.reduce((acc, tool) => {
      const values = {};
      for (const key in tool?.inputSchema?.properties) {
        values[key] = {
          ...tool?.inputSchema?.properties[key],
          required: tool?.inputSchema?.required?.includes(key)
        };
      }
      acc[tool.name] = values;
      return acc;
    }, {});
  }, [mcpToolsList]);

  const handleEditClick = () => {
    if (selectedProject?.id && testCaseId) {
      const replayUrl = `/home/projects/${selectedProject.id}/automation-studio/${testCaseId}`;
      window.open(replayUrl, '_blank');
    }
  };

  return (
    <Drawer
      open={isModalOpen}
      onClose={onClose}
      anchor="right"
      sx={{
        "& .MuiDrawer-paper": {
          borderRight: "none",
          boxShadow: "none",
          width: "75%",
          backgroundColor: "transparent",
          maxWidth: "100%",
          borderRadius: "0px",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          borderRight: "none",
          boxSizing: "border-box",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Header onClose={onClose} subtitle={headerSubtitle} aiMode={isAiDynamicRun} />
        <Box sx={{ overflow: "hidden", flexGrow: 1, minHeight: 0, p: 2, display: "flex", flexDirection: "column"}}>
          {sessionId && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingBottom: '8px' }}>
              <SessionIdCopyButton sessionId={sessionId} label="Session ID" />
            </div>
          )}
          <ExecutionSummaryHeader
            testSummary={testSummary} 
            variant="tiles" 
            showEdit={true}
            onEdit={handleEditClick}
            isAiDynamicRun={isAiDynamicRun}
          />
          <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden", paddingBottom: 1}}>
            <ExecutionResultSteps 
              executionSteps={execution_steps?.length > 0 ? execution_steps : executionSteps || []} 
              mcpToolsList={mcpToolsList}
              mcpToolMap={mcpToolMap}
              isAiDynamicRun={isAiDynamicRun}
              emptyPanelMessage={planFetchEmptyPanelMessage}
            />
          </Box>
        </Box>
      </Paper>
    </Drawer>
  );
};

export default TestLaunchExecutionDetails;
